import {
    AnomaliesCellRenderer,
    ChangeCellRenderer,
    CrossChannelProductCellWrapper,
    KeywordCellRenderer,
    PivotColumnCellRenderer,
    PriorityCellRenderer,
    ProductCellRenderer,
    tableAddSkuCellRenderer,
    TableCellRenderer,
    TableTitleCellRenderer,
    VariantsCellRenderer,
} from "@components/widgets/WidgetCellRenderers";
import { ReactComponent as DrilldownIcon } from "@images/drilldown_icon.svg";
import { capitalizeWords, titleCase } from "@ts/utils";
import { formatMetric, getRevstackMainFilterOptions } from "@/ts/revstack/helpers";
import { Tooltip, Button } from "antd";
import { CommonWidgetMetrics, OptionValues, Platforms, RevstackCASectionIds, RevstackCubeIds, RevstackDashboardModules, RevstackFilterKey, TablePerfColDefType } from "@ts/common-types";
import store from "@/store";
import { CHANNEL_CONFIGS } from "@/config/channel-configs";
import { each, keys, sortBy } from "underscore";
import { Key } from "react";
import { updateDrilldownState, updateTrendsState } from "@/features/modalSlice";
import { updateDrilldownFilters, updateTrendsFilters } from "@/features/revstackWidgetsSlice";
import { getFiltersFromCube, getSkuChannelMapping } from "../retrieve-data";
import { openPageInNewTab } from "./filters";

/**
 * !Note[DND]: getTablePerformanceColDefs and getTablePerformanceExpandedColDefs
 * * These colDefs are part of the widget config and are used to render the table
 * * Unlike drilldown, we somehow have to maintain mapping for these col as they have cellRenderers
 * * These constants will be passed through the widget config and will be used to render the table
 * * Will be easier for us to add it to backend and maintain it there...
 */

export const getMetricwiseGroupedTableColDefs = (filterToAppend: string)=>{
 //!Note: In these cols default sort is set on bases of market share can be changed dynamicaaly if required
 const METRIC_WISE_GROUPED_CHANGE_COLS =     [        {
        title: "MARKET SHARE",
        key: `${CommonWidgetMetrics.MARKET_SHARE}_group`,
        className: "dark-border-right group-header",
        children: [
            {
                title: TableTitleCellRenderer("ALL CHANNELS", "text-dark"),
                dataIndex: `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`,
                key: `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`,
                ellipsis: true,
                width: 130,
                align: "center" as const,
                defaultSortOrder: "descend",
                sorter: (a: any, b: any) =>
                    a[
                        `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`
                    ] -
                    b[
                        `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`
                    ],
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.MARKET_SHARE,
                        value,
                        record[
                            `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_change`
                        ],
                        "bottom"
                    ),
            },
            ...getChannelsTableColDefs(
                CommonWidgetMetrics.MARKET_SHARE,
                `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`,
                `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_change`
            ),
        ],
    },
    {
        title: "WT. AVAIL",
        key: "wt_availability_group",
        className: "dark-border-right group-header",
        children: [
            {
                title: TableTitleCellRenderer("ALL CHANNELS", "text-dark"),
                dataIndex: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
                key: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
                ellipsis: true,
                width: 130,
                align: "center" as const,
                sorter: (a: any, b: any) =>
                    a[
                        `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`
                    ] -
                    b[`${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`],
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                        value,
                        record[
                            `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}${filterToAppend}_change`
                        ],
                        "bottom"
                    ),
            },
            ...getChannelsTableColDefs(
                CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
                `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_change`
            ),
        ],
    },
    {
        title: "OVR. SOV",
        key: `${CommonWidgetMetrics.OVERALL_SOV}_group`,
        className: "dark-border-right group-header",
        children: [
            {
                title: TableTitleCellRenderer("ALL CHANNELS", "text-dark"),
                dataIndex: `${CommonWidgetMetrics.OVERALL_SOV}_value`,
                key: `${CommonWidgetMetrics.OVERALL_SOV}_value`,
                ellipsis: true,
                width: 130,
                align: "center" as const,
                sorter: (a: any, b: any) =>
                    a[`${CommonWidgetMetrics.OVERALL_SOV}_value`] -
                    b[`${CommonWidgetMetrics.OVERALL_SOV}_value`],
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.OVERALL_SOV,
                        value,
                        record[`${CommonWidgetMetrics.OVERALL_SOV}_change`],
                        "bottom"
                    ),
            },
            ...getChannelsTableColDefs(
                CommonWidgetMetrics.OVERALL_SOV,
                `${CommonWidgetMetrics.OVERALL_SOV}_value`,
                `${CommonWidgetMetrics.OVERALL_SOV}_change`
            ),
        ],
    },
    {
        title: "PAID. SOV",
        key: "paid_sov_group",
        className: "dark-border-right group-header",
        children: [
            {
                title: TableTitleCellRenderer("ALL CHANNELS", "text-dark"),
                dataIndex: `${CommonWidgetMetrics.PAID_SOV}_value`,
                key: `${CommonWidgetMetrics.PAID_SOV}_value`,
                ellipsis: true,
                width: 130,
                align: "center" as const,
                sorter: (a: any, b: any) =>
                    a[`${CommonWidgetMetrics.PAID_SOV}_value`] -
                    b[`${CommonWidgetMetrics.PAID_SOV}_value`],
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.OVERALL_SOV,
                        value,
                        record[`${CommonWidgetMetrics.PAID_SOV}_change`],
                        "bottom"
                    ),
            },
            ...getChannelsTableColDefs(
                CommonWidgetMetrics.PAID_SOV,
                `${CommonWidgetMetrics.PAID_SOV}_value`,
                `${CommonWidgetMetrics.PAID_SOV}_change`
            ),
        ],
    },
    {
        title: "ORG. SOV",
        key: "organic_sov_group",
        className: "dark-border-right group-header",
        children: [
            {
                title: TableTitleCellRenderer("ALL CHANNELS", "text-dark"),
                dataIndex: `${CommonWidgetMetrics.ORGANIC_SOV}_value`,
                key: `${CommonWidgetMetrics.ORGANIC_SOV}_value`,
                ellipsis: true,
                width: 130,
                align: "center" as const,
                sorter: (a: any, b: any) =>
                    a[`${CommonWidgetMetrics.ORGANIC_SOV}_value`] -
                    b[`${CommonWidgetMetrics.ORGANIC_SOV}_value`],
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.OVERALL_SOV,
                        value,
                        record[`${CommonWidgetMetrics.ORGANIC_SOV}_change`],
                        "bottom"
                    ),
            },
            ...getChannelsTableColDefs(
                CommonWidgetMetrics.ORGANIC_SOV,
                `${CommonWidgetMetrics.ORGANIC_SOV}_value`,
                `${CommonWidgetMetrics.ORGANIC_SOV}_change`
            ),
        ],
    },
    {
        title: "BRANDED SEARCH",
        key: "branded_search_group",
        className: "dark-border-right group-header",
        children: [
            {
                title: TableTitleCellRenderer("ALL CHANNELS", "text-dark"),
                dataIndex: `${CommonWidgetMetrics.BRANDED_SEARCH}_value`,
                key: `${CommonWidgetMetrics.BRANDED_SEARCH}_value`,
                ellipsis: true,
                width: 130,
                align: "center" as const,
                sorter: (a: any, b: any) =>
                    a[`${CommonWidgetMetrics.BRANDED_SEARCH}_value`] -
                    b[`${CommonWidgetMetrics.BRANDED_SEARCH}_value`],
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.BRANDED_SEARCH,
                        value,
                        record[
                            `${CommonWidgetMetrics.BRANDED_SEARCH}_change`
                        ],
                        "bottom"
                    ),
            },
            ...getChannelsTableColDefs(
                CommonWidgetMetrics.BRANDED_SEARCH,
                `${CommonWidgetMetrics.BRANDED_SEARCH}_value`,
                `${CommonWidgetMetrics.BRANDED_SEARCH}_change`
            ),
        ],
    }];
    return METRIC_WISE_GROUPED_CHANGE_COLS;
}

export const getChannelsTableColDefs = (
    metric: any,
    metricKey: any,
    changeKey: string
): any[] => {
    const channelList = store.getState().revstackChannelsList.value;
    const selectedChannelIds = store.getState().revstackChannel.value;
    const selectedChannels = channelList.filter((channel: any) => selectedChannelIds?.length ? selectedChannelIds?.includes(channel.id) : false);
    const orderedChannelList = sortBy(selectedChannels, 'view_order');

    const baseTemplate = {
        title: TableTitleCellRenderer(""),
        dataIndex: "",
        key: "",
        width: 140,
        align: "center" as const,
        render: (value: number, record: any) =>
            ChangeCellRenderer(metric, value, record[changeKey]),
    };
    return orderedChannelList.map((channel: any) => ({
        ...baseTemplate,
        title: TableTitleCellRenderer(
            CHANNEL_CONFIGS[channel.id]?.name?.toUpperCase(),
            "text-dark"
        ),
        dataIndex: [channel.id, metricKey],
        key: `channels_${channel.id}_${metricKey}`,
        sorter: (a: any, b: any) =>
            a[channel.id]?.[metricKey] - b[channel.id]?.[metricKey],
        render: (value: number, record: any) => {
            const metricValue = record[channel.id]?.[metricKey];
            const changeValue = record[channel.id]?.[changeKey];
            return ChangeCellRenderer(
                metric,
                metricValue,
                changeValue,
                "bottom"
            );
        },
    }));
};

export const drilldownClickHandler = async (params: any) => {
    const {
        primaryFilters,
        secondaryFilters = {},
        activeSectionId = null,
        module = null,
        record,
        consolidationDataFilters,
        skipStateUpdate = false,
        title = "Drill Down",
        setProdCatSubcatFilter = false,
        skuId = null,
    } = params;
    
    const drilldownFilters = store.getState().drilldownFilters.value;
    let ddFiltersUpdated = {} as any;
    let ddStateUpdated = {} as any;

    const revstackChannel = store.getState().revstackChannel.value;

    const hasSecondaryFilters = keys(secondaryFilters)?.length > 0 && secondaryFilters?.fetchOptions;

    if(keys(primaryFilters)?.length > 0){
        if(keys(primaryFilters?.overwriteParams)?.length > 0){
            ddFiltersUpdated = {
                ...ddFiltersUpdated,
                options: {
                    ...ddFiltersUpdated.options,
                    [primaryFilters?.filterKey]: primaryFilters?.overwriteParams?.options,
                },
                selected: {
                    ...ddFiltersUpdated.selected,
                    ...drilldownFilters?.selected,
                    [primaryFilters?.filterKey]: primaryFilters?.overwriteParams?.filterValue,
                }
            }

            ddStateUpdated = {
                show: true,
                activeSectionId,
                module: module,
                title: primaryFilters?.overwriteParams?.title
            }

        }else{
            const tableData = record?.dataSource || [];

            let options = tableData.map((item: any) => {
                return {
                    label: capitalizeWords(item[primaryFilters?.filterKey]),
                    subLabel: item?.[primaryFilters?.subLabelKey] ? capitalizeWords(item?.[primaryFilters?.subLabelKey]) : "",
                    value: +item[primaryFilters?.filterDataKey],
                };
            });

            options = primaryFilters?.allOptions ? [primaryFilters?.allOptions, ...options] : options;
            const filterValue = record[primaryFilters?.filterDataKey];

            ddFiltersUpdated = {
                ...ddFiltersUpdated,
                options: {
                    ...ddFiltersUpdated.options,
                    [primaryFilters?.filterKey]: options,
                },
                selected: {
                    ...ddFiltersUpdated.selected,
                    ...drilldownFilters?.selected,
                    [primaryFilters?.filterKey]: [+filterValue],
                },
            }
    
            const title = primaryFilters?.isChannelDependent
                ? record[revstackChannel[0]]?.[primaryFilters?.titleKey]
                : record[primaryFilters?.titleKey];

            ddStateUpdated = {
                show: true,
                activeSectionId,
                module: module,
                title,
            }
        }
    }

    if(hasSecondaryFilters){
        const { filterKey, filterDataKey, filterValue } = secondaryFilters;
        const { cubeId, queryId, queryDimensions, isExcludeDimension, excludeFilter, filterToSkip } = secondaryFilters?.requestParams;

        try {
            const data = await getFiltersFromCube(cubeId,queryId,queryDimensions,isExcludeDimension,excludeFilter,filterToSkip);
            const options = [] as any[];
            each(data, (item: any) => {
                options.push({
                    label: capitalizeWords(item[filterDataKey]),
                    value: item[filterDataKey],
                });
            });

            ddFiltersUpdated = {
                ...ddFiltersUpdated,
                options: {
                    ...ddFiltersUpdated.options,
                    [filterKey]: secondaryFilters?.allOptions ? [secondaryFilters?.allOptions, ...options] : options,
                },
                selected: {
                    ...ddFiltersUpdated.selected,
                    [filterKey]: filterValue
                        ? filterValue[0] !== OptionValues.ALL
                            ? [filterValue]
                            : filterValue
                        : [record[filterDataKey]],
                },
            };
        } catch (error) {
            console.error('Error fetching secondary filters:', error);
        }
    }

    if(setProdCatSubcatFilter){
        const prodCatSubcatOptions = await getSkuChannelMapping(skuId, revstackChannel[0]);

        // Note: Product category and subcategory are always single value

        ddFiltersUpdated.selected = {
            ...ddFiltersUpdated.selected,
            [CommonWidgetMetrics.CATEGORY]: [prodCatSubcatOptions?.[0]?.category_id],
            [CommonWidgetMetrics.SUB_CATEGORY]: [prodCatSubcatOptions?.[0]?.subcategory_id],
            [CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID]: [prodCatSubcatOptions?.[0]?.cross_channel_product_id || null],
        }
    }

    if(consolidationDataFilters){
        ddFiltersUpdated.consolidationDataFilters = consolidationDataFilters?.map((cd:any)=>{
            return {
                ...cd,
                condition: {
                    ...cd.condition,
                    value: record[cd.field],
                }
            }
        });
    }

    store.dispatch(updateDrilldownFilters(ddFiltersUpdated));
    
    // Only update state (open drilldown) if not skipped
    if(!skipStateUpdate){
        store.dispatch(updateDrilldownState({
            show: true,
            activeSectionId,
            module: module,
            title: title
        }));
    }
};

export const trendsClickHandler = async ({
    widgetConfig,
    titlePills,
    selectedFilterOptions,
    primaryFilters,
    secondaryFilters = [],
    sectionId
}: {
    widgetConfig: any;
    titlePills: string[];
    selectedFilterOptions: any;
    primaryFilters?: any;
    secondaryFilters?: any[];
    sectionId?: string
}) => {
    let trendsFiltersUpdated = {} as any;

    const hasSecondaryFilters = Array.isArray(secondaryFilters) && secondaryFilters.length > 0;

    // Handle primary filter by matching filterKey with selectedFilterOptions
    if (primaryFilters && primaryFilters.filterKey) {
        const primaryFilterKey = primaryFilters.filterKey;

        let matchedPrimaryKey = null;
        if (selectedFilterOptions) {
            const selectedKeys = Object.keys(selectedFilterOptions);

            matchedPrimaryKey = selectedKeys.find(key => {
                // Check if filterKey is the singular form of key (e.g., "category" matches "categories")
                const singularMatch = (key === primaryFilterKey + 's') ||
                    (key === primaryFilterKey.slice(0, -1) + 'ies' && primaryFilterKey.endsWith('y'));
                return singularMatch;
            });

        }

        if (matchedPrimaryKey && selectedFilterOptions[matchedPrimaryKey]) {
            let options = [];

            // Add allOptions first if it exists
            if (primaryFilters.allOptions) {
                options.push(primaryFilters.allOptions);
            }

            // Add the other options from selectedFilterOptions
            options = [...options, ...selectedFilterOptions[matchedPrimaryKey]];

            trendsFiltersUpdated.options = {
                ...trendsFiltersUpdated.options,
                [primaryFilterKey]: options,
            };

            if (primaryFilters.filterValue) {
                trendsFiltersUpdated.selected = {
                    ...trendsFiltersUpdated.selected,
                    [primaryFilterKey]: primaryFilters.filterValue,
                };
            }
        }
    }

    if (hasSecondaryFilters) {
        trendsFiltersUpdated.options = trendsFiltersUpdated.options || {};
        trendsFiltersUpdated.selected = trendsFiltersUpdated.selected || {};

        for (let index = 0; index < secondaryFilters.length; index++) {
            const secondaryFilter = secondaryFilters[index];

            // Special handling for two secondary filters
            if (secondaryFilters.length === 2) {

                if (index === 0) {
                    const { filterKey, filterValue, allOptions } = secondaryFilter;
                    let options = [];
                    let matchedKey = null;

                    switch (filterKey) {
                        case CommonWidgetMetrics.CATEGORY:
                            // Category logic
                            if (allOptions) {
                                options.push(allOptions);
                            }

                            // Check if any key in selectedFilterOptions includes the filterKey
                            if (selectedFilterOptions) {
                                const selectedKeys = Object.keys(selectedFilterOptions);

                                matchedKey = selectedKeys.find(key => {
                                    // Check if filterKey is the singular form of key (e.g., "category" matches "categories")
                                    const singularMatch = (key === filterKey + 's') ||
                                        (key === filterKey.slice(0, -1) + 'ies' && filterKey.endsWith('y'));
                                    return singularMatch;
                                });

                                if (matchedKey && selectedFilterOptions[matchedKey]) {
                                    options = [...options, ...selectedFilterOptions[matchedKey]];
                                }
                            }

                            if (options.length > 0) {
                                trendsFiltersUpdated.options = {
                                    ...trendsFiltersUpdated.options,
                                    [filterKey]: options,
                                };
                            }

                            trendsFiltersUpdated.selected = {
                                ...trendsFiltersUpdated.selected,
                                [filterKey]: filterValue || [],
                            };
                            break;

                        case CommonWidgetMetrics.CHANNEL:

                            if (allOptions) {
                                options.push(allOptions);
                            }

                            const revstackChannels = store.getState().revstackChannelsList.value;

                            each(revstackChannels, (channel: any) => {
                                options.push({
                                    label: titleCase(channel.name),
                                    value: channel.id,
                                    logo_url: channel.logo_url,
                                });
                            });

                            if (options.length > 0) {
                                trendsFiltersUpdated.options = {
                                    ...trendsFiltersUpdated.options,
                                    [filterKey]: options,
                                };
                            }

                            trendsFiltersUpdated.selected = {
                                ...trendsFiltersUpdated.selected,
                                [filterKey]: filterValue || [],
                            };
                            break;

                        default:
                            break;
                    }

                } else if (index === 1) {
                    // Second element: fetch data using API call
                    if (secondaryFilter?.fetchOptions) {
                        const { filterKey, filterDataKey, filterValue, allOptions } = secondaryFilter;
                        const { cubeId, queryId, queryDimensions, isExcludeDimension, excludeFilter, filterToSkip } = secondaryFilter?.requestParams;

                        try {
                            const data = await getFiltersFromCube(cubeId, queryId, queryDimensions, isExcludeDimension, excludeFilter, filterToSkip);
                            let options = [] as any[];

                            if (allOptions) {
                                options.push(allOptions);
                            }

                            each(data, (item: any) => {
                                options.push({
                                    label: item[filterDataKey],
                                    value: item[filterDataKey],
                                });
                            });
                           options = options.filter((option: any) => option.value !== "national");

                            // Update options and selected for this filter
                            trendsFiltersUpdated.options = {
                                ...trendsFiltersUpdated.options,
                                [filterKey]: options,
                            };

                            trendsFiltersUpdated.selected = {
                                ...trendsFiltersUpdated.selected,
                                [filterKey]: filterValue,
                            };

                        } catch (error) {
                            console.error(`Error fetching second secondary filter for ${filterKey}:`, error);
                        }
                    }
                }
            } else {
                // Original logic for other cases
                if (secondaryFilter?.fetchOptions) {
                    const { filterKey, filterDataKey, filterValue, allOptions } = secondaryFilter;
                    const { cubeId, queryId, queryDimensions, isExcludeDimension, excludeFilter, filterToSkip } = secondaryFilter?.requestParams;

                    try {
                        const data = await getFiltersFromCube(cubeId, queryId, queryDimensions, isExcludeDimension, excludeFilter, filterToSkip);
                        const options = [] as any[];

                        if (allOptions) {
                            options.push(allOptions);
                        }

                        each(data, (item: any) => {
                            options.push({
                                label: item[filterDataKey],
                                value: item[filterDataKey],
                            });
                        });

                        trendsFiltersUpdated.options = {
                            ...trendsFiltersUpdated.options,
                            [filterKey]: options,
                        };

                        trendsFiltersUpdated.selected = {
                            ...trendsFiltersUpdated.selected,
                            [filterKey]: filterValue,
                        };
                    } catch (error) {
                        console.error(`Error fetching secondary filter for ${filterKey}:`, error);
                    }
                } else {
                    const { filterKey, filterValue } = secondaryFilter;

                    trendsFiltersUpdated.selected = {
                        ...trendsFiltersUpdated.selected,
                        [filterKey]: filterValue || [],
                    };
                }
            }
        }
    }
    store.dispatch(updateTrendsFilters(trendsFiltersUpdated));
    store.dispatch(updateTrendsState({
        show: true,
        activeSectionId: sectionId || widgetConfig.sectionId,
        module: widgetConfig?.config?.module,
        defaultTab: widgetConfig?.config?.defaultTab,
        hideTrendsTabSection: widgetConfig?.config?.hideTrendsTabSection,
        titlePills: titlePills,
    }))
};

export const getDrilldownColDefs = (params: any) => {
    const {
        type = "drilldown",
        activeSectionId,
        module,
        relatedColDataKey,
        primaryFilters,
        secondaryFilters,
        consolidationDataFilters,
        disable = false,
        titleKey,
    } = params;

    return {
        title: "",
        key: "drilldown_action",
        dataIndex: relatedColDataKey,
        width: 50,
        align: "center" as const,
        ...(type === "drilldown" ? { fixed: "right" } : {}),
        render:
            type === "drilldown"
                ? (_: any, record: any) => (
                      <Tooltip title={disable ? "Coming Soon" : "Drilldown"}>
                          <Button
                              disabled={disable}
                              icon={
                                  <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                      <DrilldownIcon width={30} height={30} />
                                  </span>
                              }
                              className="d-flex align-items-center justify-content-center p-0"
                              style={{
                                  width: 30.8,
                                  height: 25,
                                  border: "1px solid #0078aa",
                                  color: "#0078aa",
                              }}
                              onClick={() =>
                                  drilldownClickHandler({
                                      show: true,
                                      activeSectionId,
                                      module,
                                      record,
                                      primaryFilters,
                                      secondaryFilters,
                                      consolidationDataFilters,
                                      title: titleKey ? record[titleKey] : "Drilldown",
                                  })
                              }
                          />
                      </Tooltip>
                  )
                : undefined,
    };
};

export const getTablePerformanceColDefs = (
    filterToAppend: string
): Partial<Record<TablePerfColDefType, any[]>> => ({
    ca_overview_subcategory: [
        {
            title: TableTitleCellRenderer("PRODUCTS"),
            dataIndex: "product",
            key: "product",
            width: 180,
            ellipsis: true,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record, {
                    isImageRequired: true,
                    isLinkRequired: true,
                    titleCharLimit: 10,
                }),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("OVR. SOV"),
            dataIndex: "overallSOV",
            key: "overallSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.OVERALL_SOV,
                    value,
                    record.overallSOVChange
                ),
        },
        {
            title: TableTitleCellRenderer("WT. AVAIL"),
            dataIndex: "wtAvailability",
            key: "wtAvailability",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) => {
                return ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                    value,
                    record.wtAvailabilityChange
                );
            },
        },
        {
            title: TableTitleCellRenderer("ANOMALIES"),
            dataIndex: "anomalies",
            key: "anomalies",
            width: 100,
            align: "center" as const,
            render: (anomalies: any) => AnomaliesCellRenderer(anomalies),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            align: "center" as const,
            render: (_: any, record: any) => {
                return (
                    <Tooltip title="Drilldown">
                        <Button
                            icon={
                                <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                    <DrilldownIcon width={30} height={30} />
                                </span>
                            }
                            className="d-flex align-items-center justify-content-center p-0"
                            style={{
                                width: 30.8,
                                height: 25,
                                border: "1px solid #0078aa",
                                color: "#0078aa",
                            }}
                            onClick={async () => {
                                
                                await drilldownClickHandler({
                                    skipStateUpdate: true, 
                                    setProdCatSubcatFilter: true, 
                                    skuId: record?.sku_id});

                                await drilldownClickHandler({
                                    show: true,
                                    activeSectionId: RevstackCASectionIds.PA,
                                    module: RevstackDashboardModules.CATEGORY_ANALYSIS,
                                    title: record?.sku_title,
                                    record,
                                    primaryFilters:{
                                        filterKey: CommonWidgetMetrics.SKU,
                                        filterDataKey: CommonWidgetMetrics.SKU,
                                        titleKey: CommonWidgetMetrics.SKU_TITLE,
                                        relatedColDataKey: CommonWidgetMetrics.SKU_TITLE,
                                        isChannelDependent: true
                                    },
                                    secondaryFilters:{
                                        filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                        filterDataKey: CommonWidgetMetrics.REGION,
                                        filterValue: [OptionValues.ALL],
                                        fetchOptions: true,
                                        requestParams:{
                                            cubeId: RevstackCubeIds.PO_REGIONAL,
                                            queryId: "any-id-can-be-added",
                                            queryDimensions: [RevstackFilterKey.REGION],
                                            isExcludeDimension: false,
                                            excludeFilter: [RevstackFilterKey.BRAND],
                                            filterToSkip: ""
                                        },
                                        allOptions:{
                                            label: "National",
                                            value: OptionValues.ALL
                                        },
                        
                                    },
                                    consolidationDataFilters: [
                                        {
                                            field: "subcategory_id",
                                            condition: {
                                                operater: "equals",
                                                value: null as any
                                            }
                                        },
                                        {
                                            field: "brand_id",
                                            condition: {
                                                operater: "notEquals",
                                                value: null as any
                                            }
                                        }
                                    ]
                                })
                            }}
                        />
                    </Tooltip>
                );
            }
        }

    ],
    ca_traffic_subcategory: [
        {
            title: TableTitleCellRenderer("PRODUCTS"),
            dataIndex: "product",
            key: "product",
            width: 180,
            ellipsis: true,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record, {
                    isImageRequired: true,
                    isLinkRequired: true,
                }),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("OVR. SOV"),
            dataIndex: "overallSOV",
            key: "overallSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.OVERALL_SOV,
                    value,
                    record.overallSOVChange
                ),
        },
        {
            title: TableTitleCellRenderer("PAID. SOV"),
            dataIndex: "paidSOV",
            key: "paidSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.PAID_SOV,
                    value,
                    record.paidSOVChange
                ),
        },
        {
            title: TableTitleCellRenderer("ORGANIC. SOV"),
            dataIndex: "organicSOV",
            key: "organicSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.ORGANIC_SOV,
                    value,
                    record.organicSOVChange
                ),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            align: "center" as const,
            hidden: true,
            render: () => (
                <Tooltip title="Drilldown">
                    <Button
                        icon={
                            <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                <DrilldownIcon width={30} height={30} />
                            </span>
                        }
                        className="d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: 30.8,
                            height: 25,
                            border: "1px solid #0078aa",
                            color: "#0078aa",
                        }}
                    />
                </Tooltip>
            ),
        },
    ],
    ca_traffic_keyword: [
        {
            title: TableTitleCellRenderer("KEYWORD"),
            dataIndex: "keyword",
            key: "keyword",
            width: 100,
            ellipsis: true,
            align: "left" as const,
            sorter: (a: any, b: any) => a.keyword.localeCompare(b.keyword),
            render: (text: string, record: any) =>
                KeywordCellRenderer(text, record),
        },
        {
            title: TableTitleCellRenderer("KEYWORD TYPE"),
            dataIndex: "keywordType",
            key: "keywordType",
            ellipsis: true,
            width: 80,
            align: "left" as const,
            render: (value: number, record: any) =>
                titleCase(record.keywordType),
        },
        {
            title: TableTitleCellRenderer("INDEXED SEARCH VOLUME"),
            dataIndex: "indexedSearchVolume",
            key: "indexedSearchVolume",
            ellipsis: true,
            width: 100,
            align: "right" as const,
            sorter: (a: any, b: any) =>
                a.indexedSearchVolume - b.indexedSearchVolume,
            render: (value: number, record: any) => (
                <div>
                    <span
                        className="fs-7 fw-500"
                        style={{
                            color: "#1e1b39",
                        }}
                    >
                        {formatMetric(
                            CommonWidgetMetrics.INDEXED_SEARCH_VOLUME,
                            record.indexedSearchVolume
                        )}
                    </span>
                </div>
            ),
        },
        {
            title: TableTitleCellRenderer("OVR. SOV"),
            dataIndex: "overallSOV",
            key: "overallSOV",
            ellipsis: true,
            width: 80,
            align: "right" as const,
            sorter: (a: any, b: any) => a.overallSOV - b.overallSOV,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.OVERALL_SOV,
                    value,
                    record.overallSOVChange,
                    "bottom"
                ),
        },
        {
            title: TableTitleCellRenderer("PAID. SOV"),
            dataIndex: "paidSOV",
            key: "paidSOV",
            ellipsis: true,
            width: 80,
            align: "right" as const,
            sorter: (a: any, b: any) => a.paidSOV - b.paidSOV,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.PAID_SOV,
                    value,
                    record.paidSOVChange,
                    "bottom"
                ),
        },
        {
            title: TableTitleCellRenderer("ORG. SOV"),
            dataIndex: "organicSOV",
            key: "organicSOV",
            ellipsis: true,
            width: 80,
            align: "right" as const,
            sorter: (a: any, b: any) => a.organicSOV - b.organicSOV,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.ORGANIC_SOV,
                    value,
                    record.organicSOVChange,
                    "bottom"
                ),
        },
    ],
    ca_overview_regional: [
        {
            title: TableTitleCellRenderer("SUB CATEGORIES"),
            dataIndex: "subCategory",
            key: "subCategory",
            width: 180,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record, {
                    isImageRequired: false,
                    isLinkRequired: false,
                }),
        },
        {
            title: TableTitleCellRenderer("CAT. SHARE"),
            dataIndex: "categoryShare",
            key: "categoryShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.CATEGORY_SHARE,
                    value,
                    record.categoryShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("OVR. SOV"),
            dataIndex: "overallSOV",
            key: "overallSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.OVERALL_SOV,
                    value,
                    record.overallSOVChange
                ),
        },
        {
            title: TableTitleCellRenderer("WT. AVAIL"),
            dataIndex: "wtAvailability",
            key: "wtAvailability",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                    value,
                    record.wtAvailabilityChange
                ),
        },
        {
            title: TableTitleCellRenderer("ANOMALIES"),
            dataIndex: "anomalies",
            key: "anomalies",
            width: 100,
            align: "center" as const,
            render: (anomalies: any) => AnomaliesCellRenderer(anomalies),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            hidden: true,
            align: "center" as const,
            render: () => (
                <Tooltip title="Drilldown">
                    <Button
                        icon={
                            <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                <DrilldownIcon width={30} height={30} />
                            </span>
                        }
                        className="d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: 30.8,
                            height: 25,
                            border: "1.5px solid #0078aa",
                            color: "#0078aa",
                        }}
                    />
                </Tooltip>
            ),
        },
    ],
    ca_conversion_subcategory: [
        {
            title: TableTitleCellRenderer("PRODUCTS"),
            dataIndex: "product",
            key: "product",
            width: 180,
            ellipsis: true,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record, {
                    isImageRequired: true,
                    isLinkRequired: true,
                }),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("AVG. PRICE"),
            dataIndex: "avgPricing",
            key: "avgPricing",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.AVERAGE_PRICE,
                    value,
                    record.avgPricingChange
                ),
        },
        {
            title: TableTitleCellRenderer("RPI (OVERALL)"),
            dataIndex: "rpi",
            key: "rpi",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                    value,
                    record.rpiChange
                ),
        },
        {
            title: TableTitleCellRenderer("WT. DISC."),
            dataIndex: "wtDiscount",
            key: "wtDiscount",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                    value,
                    record.wtDiscountChange
                ),
        },
        {
            title: TableTitleCellRenderer("RATINGS"),
            dataIndex: "avgRating",
            key: "avgRating",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.AVERAGE_RATING,
                    value,
                    record.avgRatingChange
                ),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            align: "center" as const,
            hidden: true,
            render: () => (
                <Tooltip title="Drilldown">
                    <Button
                        icon={
                            <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                <DrilldownIcon width={30} height={30} />
                            </span>
                        }
                        className="d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: 30.8,
                            height: 25,
                            border: "1px solid #0078aa",
                            color: "#0078aa",
                        }}
                    />
                </Tooltip>
            ),
        },
    ],
    ca_conversion_regional: [
        {
            title: TableTitleCellRenderer("SUB CATEGORIES"),
            dataIndex: "subCategory",
            key: "subCategory",
            width: 180,
            ellipsis: true,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record),
        },
        {
            title: TableTitleCellRenderer("CAT. SHARE"),
            dataIndex: "categoryShare",
            key: "categoryShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.CATEGORY_SHARE,
                    value,
                    record.categoryShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("AVG. PRICE"),
            dataIndex: "avgPricing",
            key: "avgPricing",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.AVERAGE_PRICE,
                    value,
                    record.avgPricingChange
                ),
        },
        {
            title: TableTitleCellRenderer("RPI (OVERALL)"),
            dataIndex: "rpi",
            key: "rpi",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                    value,
                    record.rpiChange
                ),
        },
        {
            title: TableTitleCellRenderer("WT DISC."),
            dataIndex: "wtDiscount",
            key: "wtDiscount",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                    value,
                    record.wtDiscountChange
                ),
        },
        {
            title: TableTitleCellRenderer("RATINGS"),
            dataIndex: "avgRating",
            key: "avgRating",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.AVERAGE_RATING,
                    value,
                    record.avgRatingChange
                ),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            align: "center" as const,
            hidden: true,
            render: () => (
                <Tooltip title="Drilldown">
                    <Button
                        icon={
                            <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                <DrilldownIcon width={30} height={30} />
                            </span>
                        }
                        className="d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: 30.8,
                            height: 25,
                            border: "1px solid #0078aa",
                            color: "#0078aa",
                        }}
                    />
                </Tooltip>
            ),
        },
    ],
    ca_operations_subcategory: [
        {
            title: TableTitleCellRenderer("PRODUCTS"),
            dataIndex: "product",
            key: "product",
            width: 180,
            ellipsis: true,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record, {
                    isImageRequired: true,
                    isLinkRequired: true,
                }),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("WT. AVAIL."),
            dataIndex: "wtAvailability",
            key: "wtAvailability",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                    value,
                    record.wtAvailabilityChange
                ),
        },
        {
            title: TableTitleCellRenderer("LISTING STATUS"),
            dataIndex: "listingStatus",
            key: "listingStatus",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.LISTING_STATUS,
                    value,
                    record.listingStatusChange
                ),
        },
        {
            title: TableTitleCellRenderer("STOCK IN HAND"),
            dataIndex: "stockInHand",
            key: "stockInHand",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.STOCK_IN_HAND,
                    value,
                    record.stockInHandChange
                ),
        },
        {
            title: TableTitleCellRenderer("FILL RATE"),
            dataIndex: "fillRate",
            key: "fillRate",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.FILL_RATE,
                    value,
                    record.fillRateChange
                ),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            align: "center" as const,
            hidden: true,
            render: () => (
                <Tooltip title="Drilldown">
                    <Button
                        icon={
                            <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                <DrilldownIcon width={30} height={30} />
                            </span>
                        }
                        className="d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: 30.8,
                            height: 25,
                            border: "1px solid #0078aa",
                            color: "#0078aa",
                        }}
                    />
                </Tooltip>
            ),
        },
    ],
    ca_operations_regional: [
        {
            title: TableTitleCellRenderer("SUB CATEGORIES"),
            dataIndex: "subCategory",
            key: "subCategory",
            width: 180,
            ellipsis: true,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record),
        },
        {
            title: TableTitleCellRenderer("CAT. SHARE"),
            dataIndex: "categoryShare",
            key: "categoryShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.CATEGORY_SHARE,
                    value,
                    record.categoryShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("WT. AVAIL."),
            dataIndex: "wtAvailability",
            key: "wtAvailability",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                    value,
                    record.wtAvailabilityChange
                ),
        },
        {
            title: TableTitleCellRenderer("LISTING STATUS"),
            dataIndex: "listingStatus",
            key: "listingStatus",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.LISTING_STATUS,
                    value,
                    record.listingStatusChange
                ),
        },
        {
            title: TableTitleCellRenderer("STOCK IN HAND"),
            dataIndex: "stockInHand",
            key: "stockInHand",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.STOCK_IN_HAND,
                    value,
                    record.stockInHandChange
                ),
        },
        {
            title: TableTitleCellRenderer("FILL RATE"),
            dataIndex: "fillRate",
            key: "fillRate",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.FILL_RATE,
                    value,
                    record.fillRateChange
                ),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            align: "center" as const,
            hidden: true,
            render: () => (
                <Tooltip title="Drilldown">
                    <Button
                        icon={
                            <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                <DrilldownIcon width={30} height={30} />
                            </span>
                        }
                        className="d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: 30.8,
                            height: 25,
                            border: "1px solid #0078aa",
                            color: "#0078aa",
                        }}
                    />
                </Tooltip>
            ),
        },
    ],
    ca_traffic_regional: [
        {
            title: TableTitleCellRenderer("SUB CATEGORIES"),
            dataIndex: "subCategory",
            key: "subCategory",
            width: 180,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record, {
                    isImageRequired: false,
                    isLinkRequired: false,
                }),
        },
        {
            title: TableTitleCellRenderer("CAT. SHARE"),
            dataIndex: "categoryShare",
            key: "categoryShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.CATEGORY_SHARE,
                    value,
                    record.categoryShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("OVR. SOV"),
            dataIndex: "overallSOV",
            key: "overallSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.OVERALL_SOV,
                    value,
                    record.overallSOVChange
                ),
        },
        {
            title: TableTitleCellRenderer("ORGANIC. SOV"),
            dataIndex: "organicSOV",
            key: "organicSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.ORGANIC_SOV,
                    value,
                    record.organicSOVChange
                ),
        },
        {
            title: TableTitleCellRenderer("PAID. SOV"),
            dataIndex: "paidSOV",
            key: "paidSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.PAID_SOV,
                    value,
                    record.paidSOVChange
                ),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            hidden: true,
            align: "center" as const,
            render: () => (
                <Tooltip title="Drilldown">
                    <Button
                        icon={
                            <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                <DrilldownIcon width={30} height={30} />
                            </span>
                        }
                        className="d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: 30.8,
                            height: 25,
                            border: "1.5px solid #0078aa",
                            color: "#0078aa",
                        }}
                    />
                </Tooltip>
            ),
        },
    ],
    pa_overview_regional: [
        {
            title: TableTitleCellRenderer("Cities"),
            dataIndex: "city",
            key: "city",
            width: 180,
            align: "left" as const,
            render: (text: string, record: any) =>
                ProductCellRenderer(text, record, {
                    isImageRequired: false,
                    isLinkRequired: false,
                }),
        },
        {
            title: TableTitleCellRenderer("MKT. SHARE"),
            dataIndex: "marketShare",
            key: "marketShare",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record.marketShareChange
                ),
        },
        {
            title: TableTitleCellRenderer("AVG. PRICE"),
            dataIndex: "avgPrice",
            key: "avgPrice",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.AVERAGE_PRICE,
                    value,
                    record.avgPriceChange
                ),
        },
        {
            title: TableTitleCellRenderer("OVR. SOV"),
            dataIndex: "overallSOV",
            key: "overallSOV",
            width: 120,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.OVERALL_SOV,
                    value,
                    record.overallSOVChange
                ),
        },
        {
            title: TableTitleCellRenderer("AVAILABILITY"),
            dataIndex: "wtAvailability",
            key: "wtAvailability",
            width: 130,
            align: "center" as const,
            render: (value: number, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                    value,
                    record.wtAvailabilityChange
                ),
        },
        {
            title: TableTitleCellRenderer("ANOMALIES"),
            dataIndex: "anomalies",
            key: "anomalies",
            width: 100,
            align: "center" as const,
            render: (anomalies: any) => AnomaliesCellRenderer(anomalies),
        },
        {
            title: "",
            key: "drilldown",
            width: 50,
            hidden: true,
            align: "center" as const,
            render: () => (
                <Tooltip title="Drilldown">
                    <Button
                        icon={
                            <span className="d-flex align-items-center justify-content-center w-100 h-100">
                                <DrilldownIcon width={30} height={30} />
                            </span>
                        }
                        className="d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: 30.8,
                            height: 25,
                            border: "1.5px solid #0078aa",
                            color: "#0078aa",
                        }}
                    />
                </Tooltip>
            ),
        },
    ],
    mi_overview_ps_ca: [
        {
            title: TableTitleCellRenderer("CATEGORIES", "text-dark"),
            dataIndex: CommonWidgetMetrics.CATEGORY,
            key: CommonWidgetMetrics.CATEGORY,
            width: 200,
            minWidth: 200,
            maxWidth: 200,
            ellipsis: true,
            fixed: "left",
            align: "left" as const,
            className: "grouped-col-cells",
            sorter: (a: any, b: any) =>
                a?.[CommonWidgetMetrics.CATEGORY]?.localeCompare(
                    b?.[CommonWidgetMetrics.CATEGORY]
                ),
            render: (text: string) =>
                PivotColumnCellRenderer({ pivotKey: text }),
        },
        ...getMetricwiseGroupedTableColDefs(filterToAppend),
        getDrilldownColDefs({
            activeSectionId: RevstackCASectionIds.CA,
            module: RevstackDashboardModules.NAVIGATOR,
            primaryFilters:{
                filterKey: CommonWidgetMetrics.CATEGORY,
                filterDataKey: `${CommonWidgetMetrics.CATEGORY}_id`,
                relatedColDataKey: CommonWidgetMetrics.CATEGORY,
                allOptions:{
                    label: "All Categories",
                    value: OptionValues.ALL
                }
            },
            secondaryFilters:{
                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                filterDataKey: CommonWidgetMetrics.REGION,
                filterValue: [OptionValues.ALL],
                fetchOptions: true,
                requestParams:{
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "any-id-can-be-added",
                    queryDimensions: [RevstackFilterKey.REGION],
                    isExcludeDimension: false,
                    excludeFilter: [RevstackFilterKey.BRAND],
                    filterToSkip: ""
                },
                allOptions:{
                    label: "National",
                    value: OptionValues.ALL
                },

            }
        }),
    ],
    mi_overview_ps_sa: [
        {
            title: TableTitleCellRenderer("SUB-CATEGORIES", "text-dark"),
            dataIndex: CommonWidgetMetrics.SUB_CATEGORY,
            key: CommonWidgetMetrics.SUB_CATEGORY,
            className: "grouped-col-cells",
            minWidth: 280,
            maxWidth: 280,
            width: 280,
            ellipsis: true,
            fixed: "left",
            align: "left" as const,
            sorter: (a: any, b: any) =>
                a?.[CommonWidgetMetrics.SUB_CATEGORY]?.localeCompare(
                    b?.[CommonWidgetMetrics.SUB_CATEGORY]
                ),
            render: (text: string, record: any) =>
                PivotColumnCellRenderer({
                    pivotKey: text,
                    pivotSubKey: record.category,
                }),
        },
        ...getMetricwiseGroupedTableColDefs(filterToAppend),
        getDrilldownColDefs({
            activeSectionId: RevstackCASectionIds.SA,
            module: RevstackDashboardModules.NAVIGATOR,
            primaryFilters:{
                filterKey: CommonWidgetMetrics.SUB_CATEGORY,
                filterDataKey: `${CommonWidgetMetrics.SUB_CATEGORY}_id`,
                relatedColDataKey: CommonWidgetMetrics.SUB_CATEGORY,
                subLabelKey: CommonWidgetMetrics.CATEGORY,
                allOptions:{
                    label: "All Subcategories",
                    value: OptionValues.ALL
                }
            },
            secondaryFilters:{
                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                filterDataKey: CommonWidgetMetrics.REGION,
                filterValue: [OptionValues.ALL],
                fetchOptions: true,
                requestParams:{
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "any-id-can-be-added",
                    queryDimensions: [RevstackFilterKey.REGION],
                    isExcludeDimension: false,
                    excludeFilter: [RevstackFilterKey.BRAND],
                    filterToSkip: ""
                },
                allOptions:{
                    label: "National",
                    value: OptionValues.ALL
                },

            }
        })
    ],
    mi_overview_ps_pa: [
        {
            title: TableTitleCellRenderer("PRODUCTS", "text-dark"),
            dataIndex: CommonWidgetMetrics.SKU_TITLE,
            key: CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
            className: "grouped-col-cells",
            width: 280,
            ellipsis: true,
            fixed: "left",
            align: "left" as const,
            sorter: (a: any, b: any) =>
                a?.[CommonWidgetMetrics.SKU_TITLE]?.localeCompare(
                    b?.[CommonWidgetMetrics.SKU_TITLE]
                ),
            render: (text: string, record: any) =>
                CrossChannelProductCellWrapper(text, record, { titleCharLimit: 18 }),
        },
        ...getMetricwiseGroupedTableColDefs(filterToAppend),
        getDrilldownColDefs({
            activeSectionId: RevstackCASectionIds.PA,
            module: RevstackDashboardModules.NAVIGATOR,
            titleKey: CommonWidgetMetrics.SKU_TITLE,
            primaryFilters:{
                filterKey: CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                filterDataKey: CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                titleKey: CommonWidgetMetrics.SKU_TITLE,
                relatedColDataKey: CommonWidgetMetrics.SKU_TITLE,
                isChannelDependent: true
            },
            secondaryFilters:{
                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                filterDataKey: CommonWidgetMetrics.REGION,
                filterValue: [OptionValues.ALL],
                fetchOptions: true,
                requestParams:{
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "any-id-can-be-added",
                    queryDimensions: [RevstackFilterKey.REGION],
                    isExcludeDimension: false,
                    excludeFilter: [RevstackFilterKey.BRAND],
                    filterToSkip: ""
                },
                allOptions:{
                    label: "National",
                    value: OptionValues.ALL
                },

            },
            consolidationDataFilters: [
                {
                    field: "subcategory_id",
                    condition: {
                        operater: "equals",
                        value: null as any
                    }
                },
                {
                    field: "brand_id",
                    condition: {
                        operater: "notEquals",
                        value: null as any
                    }
                }
            ]
        })
    ],
    mi_overview_ps_ra: [
        {
            title: TableTitleCellRenderer("REGIONS", "text-dark"),
            dataIndex: CommonWidgetMetrics.REGION,
            key: CommonWidgetMetrics.REGION,
            className: "grouped-col-cells",
            width: 180,
            ellipsis: true,
            fixed: "left",
            align: "left" as const,
            sorter: (a: any, b: any) =>
                a?.[CommonWidgetMetrics.REGION]?.localeCompare(
                    b?.[CommonWidgetMetrics.CATEGORY]
                ),
            render: (text: string) =>
                PivotColumnCellRenderer({ pivotKey: text }),
        },
        ...getMetricwiseGroupedTableColDefs(filterToAppend),
        getDrilldownColDefs({
            activeSectionId: RevstackCASectionIds.RA,
            module: RevstackDashboardModules.NAVIGATOR,
            primaryFilters:{
                filterKey: CommonWidgetMetrics.CATEGORY,
                overwriteParams: {
                    filterValue: [OptionValues.ALL],
                    options: getRevstackMainFilterOptions({type:"categories",allOptions:{
                        label: "All Categories",
                        value: OptionValues.ALL
                    }}),
                }
            },
            secondaryFilters:{
                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                filterDataKey: CommonWidgetMetrics.REGION,
                // filterValue: ["Rest Of India"],
                fetchOptions: true,
                requestParams:{
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "any-id-can-be-added",
                    queryDimensions: [RevstackFilterKey.REGION],
                    isExcludeDimension: false,
                    excludeFilter: [RevstackFilterKey.BRAND],
                    filterToSkip: ""
                }
            }
        })
    ],
});

export const getTablePerformanceExpandedColDefs = (
    filterToAppend: string
): Partial<Record<TablePerfColDefType, any[]>> => ({
    ca_overview_subcategory: [
        {
            title: "TRAFFIC",
            dataIndex: "traffic",
            key: "traffic",
            render: (traffic: any) => {
                return TableCellRenderer(
                    traffic.metric,
                    traffic.label,
                    traffic.value,
                    traffic.change,
                    traffic.isPositive
                );
            },
        },
        {
            title: "CONVERSION",
            dataIndex: "conversion",
            key: "conversion",
            render: (conversion: any) =>
                TableCellRenderer(
                    conversion.metric,
                    conversion.label,
                    conversion.value,
                    conversion.change,
                    conversion.isPositive
                ),
        },
        {
            title: "OPERATIONS",
            dataIndex: "operations",
            key: "operations",
            render: (operations: any) =>
                TableCellRenderer(
                    operations.metric,
                    operations.label,
                    operations.value,
                    operations.change,
                    operations.isPositive
                ),
        },
    ],
    ca_overview_regional: [
        {
            title: "TRAFFIC",
            dataIndex: "traffic",
            key: "traffic",
            render: (traffic: any) => {
                return TableCellRenderer(
                    traffic.metric,
                    traffic.label,
                    traffic.value,
                    traffic.change,
                    traffic.isPositive
                );
            },
        },
        {
            title: "CONVERSION",
            dataIndex: "conversion",
            key: "conversion",
            render: (conversion: any) =>
                TableCellRenderer(
                    conversion.metric,
                    conversion.label,
                    conversion.value,
                    conversion.change,
                    conversion.isPositive
                ),
        },
        {
            title: "OPERATIONS",
            dataIndex: "operations",
            key: "operations",
            render: (operations: any) =>
                TableCellRenderer(
                    operations.metric,
                    operations.label,
                    operations.value,
                    operations.change,
                    operations.isPositive
                ),
        },
    ],
    ca_traffic_subcategory: [
        {
            title: "OVERVIEW",
            dataIndex: "overview",
            key: "overview",
            render: (overview: any) => {
                return TableCellRenderer(
                    overview.metric,
                    overview.label,
                    overview.value,
                    overview.change,
                    overview.isPositive
                );
            },
        },
        {
            title: "CONVERSION",
            dataIndex: "conversion",
            key: "conversion",
            render: (conversion: any) =>
                TableCellRenderer(
                    conversion.metric,
                    conversion.label,
                    conversion.value,
                    conversion.change,
                    conversion.isPositive
                ),
        },
        {
            title: "OPERATIONS",
            dataIndex: "operations",
            key: "operations",
            render: (operations: any) =>
                TableCellRenderer(
                    operations.metric,
                    operations.label,
                    operations.value,
                    operations.change,
                    operations.isPositive
                ),
        },
    ],
    ca_conversion_subcategory: [
        {
            title: "OVERVIEW",
            dataIndex: "overview",
            key: "overview",
            render: (overview: any) => {
                return TableCellRenderer(
                    overview.metric,
                    overview.label,
                    overview.value,
                    overview.change,
                    overview.isPositive
                );
            },
        },
        {
            title: "TRAFFIC",
            dataIndex: "traffic",
            key: "traffic",
            render: (traffic: any) =>
                TableCellRenderer(
                    traffic.metric,
                    traffic.label,
                    traffic.value,
                    traffic.change,
                    traffic.isPositive
                ),
        },
        {
            title: "OPERATIONS",
            dataIndex: "operations",
            key: "operations",
            render: (operations: any) =>
                TableCellRenderer(
                    operations.metric,
                    operations.label,
                    operations.value,
                    operations.change,
                    operations.isPositive
                ),
        },
    ],
    ca_conversion_regional: [
        {
            title: "OVERVIEW",
            dataIndex: "overview",
            key: "overview",
            render: (overview: any) => {
                return TableCellRenderer(
                    overview.metric,
                    overview.label,
                    overview.value,
                    overview.change,
                    overview.isPositive
                );
            },
        },
        {
            title: "TRAFFIC",
            dataIndex: "traffic",
            key: "traffic",
            render: (traffic: any) =>
                TableCellRenderer(
                    traffic.metric,
                    traffic.label,
                    traffic.value,
                    traffic.change,
                    traffic.isPositive
                ),
        },
        {
            title: "OPERATIONS",
            dataIndex: "operations",
            key: "operations",
            render: (operations: any) =>
                TableCellRenderer(
                    operations.metric,
                    operations.label,
                    operations.value,
                    operations.change,
                    operations.isPositive
                ),
        },
    ],
    ca_operations_subcategory: [
        {
            title: "OVERVIEW",
            dataIndex: "overview",
            key: "overview",
            render: (overview: any) => {
                return TableCellRenderer(
                    overview.metric,
                    overview.label,
                    overview.value,
                    overview.change,
                    overview.isPositive
                );
            },
        },
        {
            title: "TRAFFIC",
            dataIndex: "traffic",
            key: "traffic",
            render: (traffic: any) =>
                TableCellRenderer(
                    traffic.metric,
                    traffic.label,
                    traffic.value,
                    traffic.change,
                    traffic.isPositive
                ),
        },
        {
            title: "CONVERSION",
            dataIndex: "conversion",
            key: "conversion",
            render: (conversion: any) =>
                TableCellRenderer(
                    conversion.metric,
                    conversion.label,
                    conversion.value,
                    conversion.change,
                    conversion.isPositive
                ),
        },
    ],
    ca_operations_regional: [
        {
            title: "OVERVIEW",
            dataIndex: "overview",
            key: "overview",
            render: (overview: any) => {
                return TableCellRenderer(
                    overview.metric,
                    overview.label,
                    overview.value,
                    overview.change,
                    overview.isPositive
                );
            },
        },
        {
            title: "TRAFFIC",
            dataIndex: "traffic",
            key: "traffic",
            render: (traffic: any) =>
                TableCellRenderer(
                    traffic.metric,
                    traffic.label,
                    traffic.value,
                    traffic.change,
                    traffic.isPositive
                ),
        },
        {
            title: "CONVERSION",
            dataIndex: "conversion",
            key: "conversion",
            render: (conversion: any) =>
                TableCellRenderer(
                    conversion.metric,
                    conversion.label,
                    conversion.value,
                    conversion.change,
                    conversion.isPositive
                ),
        },
    ],
    ca_traffic_regional: [
        {
            title: "OVERVIEW",
            dataIndex: "overview",
            key: "overview",
            render: (overview: any) => {
                return TableCellRenderer(
                    overview.metric,
                    overview.label,
                    overview.value,
                    overview.change,
                    overview.isPositive
                );
            },
        },
        {
            title: "CONVERSION",
            dataIndex: "conversion",
            key: "conversion",
            render: (conversion: any) =>
                TableCellRenderer(
                    conversion.metric,
                    conversion.label,
                    conversion.value,
                    conversion.change,
                    conversion.isPositive
                ),
        },
        {
            title: "OPERATIONS",
            dataIndex: "operations",
            key: "operations",
            render: (operations: any) =>
                TableCellRenderer(
                    operations.metric,
                    operations.label,
                    operations.value,
                    operations.change,
                    operations.isPositive
                ),
        },
    ],
    ca_traffic_keyword: [],
    pa_overview_regional: [
        {
            title: "TRAFFIC",
            dataIndex: "traffic",
            key: "traffic",
            render: (traffic: any) => {
                return TableCellRenderer(
                    traffic.metric,
                    traffic.label,
                    traffic.value,
                    traffic.change,
                    traffic.isPositive
                );
            },
        },
        {
            title: "CONVERSION",
            dataIndex: "conversion",
            key: "conversion",
            render: (conversion: any) =>
                TableCellRenderer(
                    conversion.metric,
                    conversion.label,
                    conversion.value,
                    conversion.change,
                    conversion.isPositive
                ),
        },
        {
            title: "OPERATIONS",
            dataIndex: "operations",
            key: "operations",
            render: (operations: any) =>
                TableCellRenderer(
                    operations.metric,
                    operations.label,
                    operations.value,
                    operations.change,
                    operations.isPositive
                ),
        },
    ],
    mi_overview_ps_ca: [
        {
            title: TableTitleCellRenderer("SUB-CATEGORIES", "text-dark"),
            dataIndex: CommonWidgetMetrics.SUB_CATEGORY,
            key: CommonWidgetMetrics.SUB_CATEGORY,
            width: 200,
            minWidth: 200,
            maxWidth: 200,
            ellipsis: true,
            fixed: "left",
            align: "left" as const,
            className: "grouped-col-cells",
            sorter: (a: any, b: any) =>
                a?.[CommonWidgetMetrics.SUB_CATEGORY]?.localeCompare(
                    b?.[CommonWidgetMetrics.SUB_CATEGORY]
                ),
            render: (text: string) =>
                PivotColumnCellRenderer({ pivotKey: text }),
        },
        ...getMetricwiseGroupedTableColDefs(filterToAppend),
        getDrilldownColDefs({
            type: "whitespace"
        })
    ],
    mi_overview_ps_sa:
    [{
        title: TableTitleCellRenderer("PRODUCTS", "text-dark"),
        dataIndex: CommonWidgetMetrics.SKU_TITLE,
        key: CommonWidgetMetrics.SKU_TITLE,
        className: "grouped-col-cells",
        minWidth: 280,
        maxWidth: 280,
        width: 280,
        ellipsis: true,
        fixed: "left",
        align: "left" as const,
        sorter: (a: any, b: any) =>
            a?.[CommonWidgetMetrics.SKU_TITLE]?.localeCompare(
                b?.[CommonWidgetMetrics.SKU_TITLE]
            ),
            render: (text: string, record: any) =>
                ProductCellRenderer(titleCase(text), record, {
                    isImageRequired: true,
                    isLinkRequired: true,
                }),
    },
    ...getMetricwiseGroupedTableColDefs(filterToAppend),
    getDrilldownColDefs({
        type: "whitespace"
    })
],
    mi_overview_ps_pa: [],
    mi_overview_ps_ra: [
        {
            title: TableTitleCellRenderer("CITIES", "text-dark"),
            dataIndex: CommonWidgetMetrics.CITY,
            key: CommonWidgetMetrics.CITY,
            className: "grouped-col-cells",
            width: 180,
            minWidth: 180,
            maxWidth: 180,
            ellipsis: true,
            fixed: "left",
            align: "left" as const,
            sorter: (a: any, b: any) =>
                a?.[CommonWidgetMetrics.CITY]?.localeCompare(
                    b?.[CommonWidgetMetrics.CITY]
                ),
            render: (text: string) =>
                PivotColumnCellRenderer({ pivotKey: text }),
        },
        ...getMetricwiseGroupedTableColDefs(filterToAppend),
        getDrilldownColDefs({
            type: "whitespace"
        })
    ],
    dd_mi_ov_ps_bd: [
        {
            title: TableTitleCellRenderer("SUB-CATEGORIES", "text-dark"),
            dataIndex: CommonWidgetMetrics.SUB_CATEGORY,
            key: CommonWidgetMetrics.SUB_CATEGORY,
            minWidth: 191,
            maxWidth: 191,
            width: 191,
            ellipsis: true,
            fixed: "left",
            align: "left" as const,
            className: "grouped-col-cells",
            sorter: (a: any, b: any) =>
                a?.[CommonWidgetMetrics.SUB_CATEGORY]?.localeCompare(
                    b?.[CommonWidgetMetrics.SUB_CATEGORY]
                ),
            render: (text: string) =>
                PivotColumnCellRenderer({ pivotKey: text }),
        },
        ...getMetricwiseGroupedTableColDefs(filterToAppend),
    ]
});

//TODO: Refactor this fn and add enumerations in this...

export const PINNED_COL_MAP = {
    [CommonWidgetMetrics.SUB_CATEGORY]: {
        title: "SUB-CATEGORY",
        dataIndex: "subcategory",
        key: "subcategory",
        className: "grouped-col-cells",
        fixed: "left",
        minWidth: 221,
        maxWidth: 221,
        width: 221,
        align: "left",
        sorter: (a: any, b: any) => {
            const aVal = a.subcategory || "";
            const bVal = b.subcategory || "";
            return aVal.localeCompare(bVal);
        },
        render: (text: string) => (
            <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                {titleCase(text)}
            </span>
        ),
    },
    [CommonWidgetMetrics.BRAND]: {
        title: "BRAND",
        dataIndex: "brand",
        key: "brand",
        fixed: "left",
        minWidth: 191,
        maxWidth: 191,
        width: 191,
        align: "left",
        sorter: (a: any, b: any) => {
            const aVal = a.brand || "";
            const bVal = b.brand || "";
            return aVal.localeCompare(bVal);
        },
        render: (text: string) => (
            <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                {titleCase(text)}
            </span>
        ),
        className: "grouped-col-cells",
    },
    [CommonWidgetMetrics.SKU]: {
        title: "PRODUCT",
        dataIndex: "sku_title",
        key: "product",
        fixed: "left",
        minWidth: 344,
        maxWidth: 344,
        width: 344,
        align: "left",
        sorter: (a: any, b: any) => {
            const aVal = a.product || a.sku_title || "";
            const bVal = b.product || b.sku_title || "";
            return aVal.localeCompare(bVal);
        },
        render: (text: string, record: any) =>
            ProductCellRenderer(titleCase(text), record, {
                isImageRequired: true,
                isLinkRequired: true,
            }),
        className: "grouped-col-cells",
    },
    [CommonWidgetMetrics.REGION]: {
        title: "REGION",
        dataIndex: "region",
        key: "region",
        fixed: "left",
        minWidth: 191,
        maxWidth: 191,
        width: 191,
        align: "left",
        render: (text: string) => (
            <span style={{ fontWeight: 600 }}>{titleCase(text)}</span>
        ),
        className: "metric-header",
    },
    [CommonWidgetMetrics.KEYWORD_NAME]: {
        title: "KEYWORD ANALYSIS",
        dataIndex: "keyword",
        key: "keyword",
        fixed: "left",
        minWidth: 221,
        maxWidth: 221,
        width: 221,
        align: "left",
        sorter: (a: any, b: any) => {
            const aVal = a.keyword || "";
            const bVal = b.keyword || "";
            return aVal.localeCompare(bVal);
        },
        render: (text: string) => (
            <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                {capitalizeWords(text)}
            </span>
        ),
        filters: [] as any,
        onFilter: (value: boolean | Key, record: any) =>
            record.keyword === value,
        className: "grouped-col-cells",
    },
    [CommonWidgetMetrics.CHANNEL]: {
        title: "CHANNEL",
        dataIndex: "channel_id",
        key: "channel",
        fixed: "left",
        minWidth: 191,
        maxWidth: 191,
        width: 191,
        align: "left",
        render: (text: string, record: any, index: number) => {
            return (
                <span style={{ fontWeight: 600 }}>
                    {titleCase(CHANNEL_CONFIGS[parseInt(text)]?.name)}
                </span>
            );
        },
        className: "metric-header",
    },
};
export const getRevstackDDTableColDefs = (filterToAppend: string) => {
    const METRICS_COL_MAP = {
        [CommonWidgetMetrics.MARKET_SHARE]: {
            title: "MARKET SHARE",
            dataIndex: `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`,
            key: "market_share",
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record[
                        `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_change`
                    ],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[
                            `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`
                        ]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[
                            `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`
                        ]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.AVERAGE_PRICE]: {
            title: "AVG. PRICE",
            dataIndex: `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
            key: CommonWidgetMetrics.AVERAGE_PRICE,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.AVERAGE_PRICE,
                    value,
                    record[`${CommonWidgetMetrics.AVERAGE_PRICE}_change`],
                    "bottom"
            ),
        },
        [CommonWidgetMetrics.OVERALL_SOV]: {
            title: "OVERALL SOV",
            dataIndex: `${CommonWidgetMetrics.OVERALL_SOV}_value`,
            key: CommonWidgetMetrics.OVERALL_SOV,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.OVERALL_SOV,
                    value,
                    record[`${CommonWidgetMetrics.OVERALL_SOV}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.OVERALL_SOV}_value`]) ||
                    0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.OVERALL_SOV}_value`]) ||
                    0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.WEIGHTED_AVAILABILITY]: {
            title: "WT. AVAIL",
            dataIndex: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
            key: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                    value,
                    record[
                        `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_change`
                    ],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.ORGANIC_SOV]: {
            title: "ORGANIC SOV",
            dataIndex: `${CommonWidgetMetrics.ORGANIC_SOV}_value`,
            key: CommonWidgetMetrics.ORGANIC_SOV,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.ORGANIC_SOV,
                    value,
                    record[`${CommonWidgetMetrics.ORGANIC_SOV}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.ORGANIC_SOV}_value`]) ||
                    0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.ORGANIC_SOV}_value`]) ||
                    0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.PAID_SOV]: {
            title: "PAID SOV",
            dataIndex: `${CommonWidgetMetrics.PAID_SOV}_value`,
            key: CommonWidgetMetrics.PAID_SOV,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.PAID_SOV,
                    value,
                    record[`${CommonWidgetMetrics.PAID_SOV}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.PAID_SOV}_value`]) || 0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.PAID_SOV}_value`]) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.BRANDED_SEARCH]: {
            title: "BRANDED SEARCH",
            dataIndex: `${CommonWidgetMetrics.BRANDED_SEARCH}_value`,
            key: CommonWidgetMetrics.BRANDED_SEARCH,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.BRANDED_SEARCH,
                    value,
                    record[`${CommonWidgetMetrics.BRANDED_SEARCH}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.BRANDED_SEARCH}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.BRANDED_SEARCH}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.PAID_IMPRESSION]: {
            title: "PAID IMPRESSIONS",
            dataIndex: `${CommonWidgetMetrics.PAID_IMPRESSION}_value`,
            key: CommonWidgetMetrics.PAID_IMPRESSION,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.PAID_IMPRESSION,
                    value,
                    record[`${CommonWidgetMetrics.PAID_IMPRESSION}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.PAID_IMPRESSION}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.PAID_IMPRESSION}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.RELATIVE_PRICE_INDEX]: {
            title: "RPI",
            dataIndex: `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_value`,
            key: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                    value,
                    record[
                        `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_change`
                    ],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.AVERAGE_RATING]: {
            title: "RATING",
            dataIndex: `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
            key: CommonWidgetMetrics.AVERAGE_RATING,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.AVERAGE_RATING,
                    value,
                    record[`${CommonWidgetMetrics.AVERAGE_RATING}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.AVERAGE_RATING}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.AVERAGE_RATING}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.CONTENT_SCORE]: {
            title: "CONTENT SCORE",
            dataIndex: `${CommonWidgetMetrics.CONTENT_SCORE}_value`,
            key: CommonWidgetMetrics.CONTENT_SCORE,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.CONTENT_SCORE,
                    value,
                    record[`${CommonWidgetMetrics.CONTENT_SCORE}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.CONTENT_SCORE}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.CONTENT_SCORE}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.STOCK_IN_HAND]: {
            title: "STOCK IN HAND",
            dataIndex: `${CommonWidgetMetrics.STOCK_IN_HAND}_value`,
            key: CommonWidgetMetrics.STOCK_IN_HAND,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.STOCK_IN_HAND,
                    value,
                    record[`${CommonWidgetMetrics.STOCK_IN_HAND}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.STOCK_IN_HAND}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.STOCK_IN_HAND}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.FILL_RATE]: {
            title: "FILL RATE",
            dataIndex: `${CommonWidgetMetrics.FILL_RATE}_value`,
            key: CommonWidgetMetrics.FILL_RATE,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.FILL_RATE,
                    value,
                    record[`${CommonWidgetMetrics.FILL_RATE}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.FILL_RATE}_value`]) ||
                    0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.FILL_RATE}_value`]) ||
                    0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.LISTING_STATUS]: {
            title: "LISTING STATUS",
            dataIndex: `${CommonWidgetMetrics.LISTING_STATUS}_value`,
            key: CommonWidgetMetrics.LISTING_STATUS,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.LISTING_STATUS,
                    value,
                    record[`${CommonWidgetMetrics.LISTING_STATUS}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.LISTING_STATUS}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.LISTING_STATUS}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.WEIGHTED_DISCOUNT]: {
            title: "WT. DISCOUNT",
            dataIndex: `${CommonWidgetMetrics.WEIGHTED_DISCOUNT}_value`,
            key: CommonWidgetMetrics.WEIGHTED_DISCOUNT,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                    value,
                    record[`${CommonWidgetMetrics.WEIGHTED_DISCOUNT}_change`],
                    "bottom"
            ),
        },
        [CommonWidgetMetrics.KEYWORD_INTENT]: {
            title: "KEYWORD TYPE",
            dataIndex: `${CommonWidgetMetrics.KEYWORD_INTENT}`,
            key: CommonWidgetMetrics.KEYWORD_INTENT,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (text: string) => (
                <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                    {titleCase(text)}
                </span>
            ),
        },
        [CommonWidgetMetrics.INDEXED_SEARCH_VOLUME]: {
            title: "INDEXED SEARCH VOLUME",
            dataIndex: `${CommonWidgetMetrics.INDEXED_SEARCH_VOLUME}_value`,
            key: CommonWidgetMetrics.INDEXED_SEARCH_VOLUME,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.INDEXED_SEARCH_VOLUME,
                    value,
                    record[
                        `${CommonWidgetMetrics.INDEXED_SEARCH_VOLUME}_change`
                    ],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.INDEXED_SEARCH_VOLUME}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.INDEXED_SEARCH_VOLUME}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.SEARCH_INDEX]: {
            title: "INDEXED SEARCH VOLUME",
            dataIndex: `${CommonWidgetMetrics.SEARCH_INDEX}_value`,
            key: CommonWidgetMetrics.SEARCH_INDEX,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.SEARCH_INDEX,
                    value,
                    record[
                    `${CommonWidgetMetrics.SEARCH_INDEX}_change`
                    ],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.SEARCH_INDEX}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.SEARCH_INDEX}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.KEYWORD_OVERALL_SOV]: {
            title: "OVERALL SOV",
            dataIndex: `${CommonWidgetMetrics.KEYWORD_OVERALL_SOV}_value`,
            key: CommonWidgetMetrics.KEYWORD_OVERALL_SOV,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.KEYWORD_OVERALL_SOV,
                    value,
                    record[`${CommonWidgetMetrics.KEYWORD_OVERALL_SOV}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.KEYWORD_OVERALL_SOV}_value`]) ||
                    0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.KEYWORD_OVERALL_SOV}_value`]) ||
                    0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.KEYWORD_ORGANIC_SOV]: {
            title: "ORGANIC SOV",
            dataIndex: `${CommonWidgetMetrics.KEYWORD_ORGANIC_SOV}_value`,
            key: CommonWidgetMetrics.KEYWORD_ORGANIC_SOV,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.KEYWORD_ORGANIC_SOV,
                    value,
                    record[`${CommonWidgetMetrics.KEYWORD_ORGANIC_SOV}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.KEYWORD_ORGANIC_SOV}_value`]) ||
                    0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.KEYWORD_ORGANIC_SOV}_value`]) ||
                    0;
                return aVal - bVal;
            },
        },
        [CommonWidgetMetrics.KEYWORD_PAID_SOV]: {
            title: "PAID SOV",
            dataIndex: `${CommonWidgetMetrics.KEYWORD_PAID_SOV}_value`,
            key: CommonWidgetMetrics.KEYWORD_PAID_SOV,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.KEYWORD_PAID_SOV,
                    value,
                    record[`${CommonWidgetMetrics.KEYWORD_PAID_SOV}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.KEYWORD_PAID_SOV}_value`]) ||
                    0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.KEYWORD_PAID_SOV}_value`]) ||
                    0;
                return aVal - bVal;
            },
        },
    };
    const revstackDDColDefs: Record<string, any[]> = {
        dd_ca_ov_sp_ov: [
            PINNED_COL_MAP[CommonWidgetMetrics.SUB_CATEGORY],
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
        ],
        dd_ca_ov_sp_bd: [
            PINNED_COL_MAP[CommonWidgetMetrics.BRAND],
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                order: 0,
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
        ],
        dd_ca_ov_sp_sku: [
            {
                ...PINNED_COL_MAP[CommonWidgetMetrics.SKU],
                render: (text: string, record: any) =>
                    ProductCellRenderer(titleCase(text), record, {
                        isImageRequired: true,
                        isLinkRequired: true,
                        titleCharLimit: 28,
                    }),
            },
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
        ],
        dd_ca_ov_sp_cr: [
            PINNED_COL_MAP[CommonWidgetMetrics.REGION],
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                order: 0,
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
        ],
        dd_ca_ov_sp_cc: [
            PINNED_COL_MAP[CommonWidgetMetrics.CHANNEL],
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                order: 0,
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
        ],
        dd_ca_traffic_sp_ov: [
            PINNED_COL_MAP[CommonWidgetMetrics.SUB_CATEGORY],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_IMPRESSION],
        ],
        dd_ca_traffic_sp_bd: [
            PINNED_COL_MAP[CommonWidgetMetrics.BRAND],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_IMPRESSION]
        ],
        dd_ca_traffic_sp_sku: [
            PINNED_COL_MAP[CommonWidgetMetrics.SKU],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_IMPRESSION],
        ],
        dd_ca_traffic_sp_kwd: [
            PINNED_COL_MAP[CommonWidgetMetrics.KEYWORD_NAME],
            METRICS_COL_MAP[CommonWidgetMetrics.KEYWORD_TYPE],
            METRICS_COL_MAP[CommonWidgetMetrics.SEARCH_INDEX],
            METRICS_COL_MAP[CommonWidgetMetrics.KEYWORD_OVERALL_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.KEYWORD_ORGANIC_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.KEYWORD_PAID_SOV],
        ],
        dd_ca_traffic_sp_cr: [
            PINNED_COL_MAP[CommonWidgetMetrics.REGION],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
        ],
        dd_ca_traffic_sp_cc: [
            PINNED_COL_MAP[CommonWidgetMetrics.CHANNEL],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_IMPRESSION],
        ],
        dd_ca_conversion_sp_ov: [
            PINNED_COL_MAP[CommonWidgetMetrics.SUB_CATEGORY],
            METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
            METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
        ],
        dd_ca_conversion_sp_bd: [
            PINNED_COL_MAP[CommonWidgetMetrics.BRAND],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
            METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_DISCOUNT],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
            METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
        ],
        dd_ca_conversion_sp_sku: [
            PINNED_COL_MAP[CommonWidgetMetrics.SKU],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
            METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_DISCOUNT],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
        ],
        dd_ca_conversion_sp_cr: [
            PINNED_COL_MAP[CommonWidgetMetrics.REGION],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
            METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_DISCOUNT],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
            METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
        ],
        dd_ca_conversion_sp_cc: [
            PINNED_COL_MAP[CommonWidgetMetrics.CHANNEL],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
            METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_DISCOUNT],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
            METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
        ],
        dd_ca_operations_sp_ov: [
            PINNED_COL_MAP[CommonWidgetMetrics.SUB_CATEGORY],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
            METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
            METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
            METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
        ],
        dd_ca_operations_sp_bd: [
            PINNED_COL_MAP[CommonWidgetMetrics.BRAND],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
            METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
            METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
        ],
        dd_ca_operations_sp_sku: [
            PINNED_COL_MAP[CommonWidgetMetrics.SKU],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
            METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
            METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
            METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
        ],
        dd_ca_operations_sp_cr: [
            PINNED_COL_MAP[CommonWidgetMetrics.REGION],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
            METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
            METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
            METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
        ],
        dd_ca_operations_sp_cc: [
            PINNED_COL_MAP[CommonWidgetMetrics.CHANNEL],
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
            METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
            METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
            METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
        ],
        dd_mi_ov_ps_sc:[
            {
                title: TableTitleCellRenderer("SUB-CATEGORIES", "text-dark"),
                dataIndex: CommonWidgetMetrics.SUB_CATEGORY,
                key: CommonWidgetMetrics.SUB_CATEGORY,
                minWidth: 221,
                maxWidth: 221,
                width: 221,
                ellipsis: true,
                fixed: "left",
                align: "left" as const,
                className: "grouped-col-cells",
                sorter: (a: any, b: any) =>
                    a?.[CommonWidgetMetrics.SUB_CATEGORY]?.localeCompare(
                        b?.[CommonWidgetMetrics.SUB_CATEGORY]
                    ),
                render: (text: string) =>
                    PivotColumnCellRenderer({ pivotKey: text }),
            },
            ...getMetricwiseGroupedTableColDefs(filterToAppend),
        ],
        dd_mi_ov_ps_bd: [
            {
                title: TableTitleCellRenderer("BRANDS", "text-dark"),
                dataIndex: CommonWidgetMetrics.BRAND,
                key: CommonWidgetMetrics.BRAND,
                minWidth: 191,
                maxWidth: 191,
                width: 191,
                ellipsis: true,
                fixed: "left",
                align: "left" as const,
                className: "grouped-col-cells",
                sorter: (a: any, b: any) =>
                    a?.[CommonWidgetMetrics.BRAND]?.localeCompare(
                        b?.[CommonWidgetMetrics.BRAND]
                    ),
                render: (text: string) =>
                    PivotColumnCellRenderer({ pivotKey: text }),
            },
            ...getMetricwiseGroupedTableColDefs(filterToAppend),
        ],
        dd_mi_ov_ps_sku: [
            {
                title: TableTitleCellRenderer("PRODUCTS", "text-dark"),
                dataIndex: CommonWidgetMetrics.SKU_TITLE,
                key: CommonWidgetMetrics.SKU_TITLE,
                minWidth: 344,
                maxWidth: 344,
                width: 344,
                ellipsis: true,
                fixed: "left",
                align: "left" as const,
                className: "grouped-col-cells",
                sorter: (a: any, b: any) =>
                    a?.[CommonWidgetMetrics.SKU_TITLE]?.localeCompare(
                        b?.[CommonWidgetMetrics.SKU_TITLE]
                    ),
                render: (text: string, record: any) =>
                    CrossChannelProductCellWrapper(text, record),
            },
            ...getMetricwiseGroupedTableColDefs(filterToAppend),
        ],
        dd_mi_ov_ps_cc: [
            PINNED_COL_MAP[CommonWidgetMetrics.CHANNEL],
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                order: 0,
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
            ...getMetricwiseGroupedTableColDefs(filterToAppend),
        ],
        dd_mi_ov_ps_cr: [
            {
                title: TableTitleCellRenderer("REGION", "text-dark"),
                dataIndex: CommonWidgetMetrics.REGION,
                key: CommonWidgetMetrics.REGION,
                minWidth: 191,
                maxWidth: 191,
                width: 191,
                ellipsis: true,
                fixed: "left",
                align: "left" as const,
                className: "grouped-col-cells",
                sorter: (a: any, b: any) =>
                    a?.[CommonWidgetMetrics.REGION]?.localeCompare(
                        b?.[CommonWidgetMetrics.REGION]
                    ),
                render: (text: string) =>
                    PivotColumnCellRenderer({ pivotKey: text }),
            },
            ...getMetricwiseGroupedTableColDefs(filterToAppend),
        ],
        cc_sku_list_component: [
            {...PINNED_COL_MAP[CommonWidgetMetrics.SKU]},
            {
                title: "Priority",
                dataIndex: `${CommonWidgetMetrics.PRIORITY}_value`,
                key: CommonWidgetMetrics.PRIORITY,
                minWidth: 232,
                maxWidth: 232,
                width: 232,
                align: "right",
        render: (value: any, record: any) =>
            PriorityCellRenderer(record[`${CommonWidgetMetrics.PRIORITY}_value`], record),
            },
            {
            title: "Variants",
            dataIndex: `${CommonWidgetMetrics.VARIANTS}_value`,
            key: CommonWidgetMetrics.VARIANTS,
            minWidth: 232,
            maxWidth: 232,
            width: 232,
            align: "right",
            render: (value: any, record: any) =>
                <VariantsCellRenderer
                    value={record[`${CommonWidgetMetrics.VARIANTS}_value`]}
                    record={record}
                />,
            },
            {...METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE]},
            {
            title: "WT. AVAIL",
            dataIndex: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
            key: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "right",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                    value,
                    record[
                        `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_change`
                    ],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`]
                    ) || 0;
                return aVal - bVal;
            },
            },
        ],
        change_sku: [
            {    
                ...PINNED_COL_MAP[CommonWidgetMetrics.SKU],
                minWidth: 300,
                maxWidth: 300,
                width: 300
            },
            {
                ...METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                minWidth: 130,
                maxWidth: 150,
                width: 150,
            },
            {
                ...METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV]
            },
            {
                ...METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY]
            },
            {
                title: "",
                dataIndex: `${CommonWidgetMetrics.MARKET_SHARE}${filterToAppend}_value`,
                key: "add_sku",
                minWidth: 130,
                maxWidth: 130,
                width: 130,
                align: "center",
                render: (value: any, record: any, selectedChangeSku: string | null) => 
                {
                    const relativeFilters = store.getState().relativeFilters.value;
                    const relativeFiltersSku = relativeFilters.skus?.[0];

                    const selectedSku  = selectedChangeSku === null ? relativeFiltersSku: selectedChangeSku;
                    return tableAddSkuCellRenderer(record, selectedSku)
                }
                    
            }
        ],
        dd_po_cta: [
            {...PINNED_COL_MAP[CommonWidgetMetrics.SKU],
                render: (text: string, record: any) =>
                    ProductCellRenderer(titleCase(text), record, {
                        isImageRequired: true,
                        isLinkRequired: true,
                        titleCharLimit: 30,
                    }),

            },
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    {...METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY], title: "AVAILABILITY"},
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
        ],
        dd_po_cra:[
            PINNED_COL_MAP[CommonWidgetMetrics.REGION],
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    {...METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY], title: "AVAILABILITY"},
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
        ],
        dd_po_cca:[
            PINNED_COL_MAP[CommonWidgetMetrics.CHANNEL],
            {
                title: "Overall Performance",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
                    {...METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY], title: "AVAILABILITY"},
                    METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
                ],
            },
            {
                title: "Traffic",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
                    METRICS_COL_MAP[CommonWidgetMetrics.BRANDED_SEARCH],
                ],
            },
            {
                title: "Conversion",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
                    METRICS_COL_MAP[CommonWidgetMetrics.RELATIVE_PRICE_INDEX],
                    METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_RATING],
                    METRICS_COL_MAP[CommonWidgetMetrics.CONTENT_SCORE],
                ],
            },
            {
                title: "Operations",
                align: "center",
                className: "dark-border-right group-header",
                children: [
                    METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
                    METRICS_COL_MAP[CommonWidgetMetrics.FILL_RATE],
                    METRICS_COL_MAP[CommonWidgetMetrics.LISTING_STATUS],
                ],
            },
        ],
        sku_selection:[

            {...PINNED_COL_MAP[CommonWidgetMetrics.SKU],
                align: "left",
                render: (text: string, record: any) =>
                    ProductCellRenderer(titleCase(text), record, {
                        isImageRequired: true,
                        isLinkRequired: true,
                        size:"medium"
                    }),
            },
            METRICS_COL_MAP[CommonWidgetMetrics.MARKET_SHARE],
            METRICS_COL_MAP[CommonWidgetMetrics.OVERALL_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.ORGANIC_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.PAID_SOV],
            METRICS_COL_MAP[CommonWidgetMetrics.AVERAGE_PRICE],
            METRICS_COL_MAP[CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
            METRICS_COL_MAP[CommonWidgetMetrics.STOCK_IN_HAND],
        ]
    };
    return revstackDDColDefs;
};